import React, { useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "./threeMonth.css";

dayjs.extend(customParseFormat);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const parseCommentDate = (val) => {
  // Accept Date, Excel serial, or string in many formats
  if (val instanceof Date && !isNaN(val)) return dayjs(val);
  if (typeof val === "number" && isFinite(val)) {
    // Excel serial date (epoch 1899-12-30)
    const base = new Date(1899, 11, 30);
    return dayjs(base).add(val, "day");
  }
  if (typeof val === "string") {
    const s = val.trim();
    const formats = [
      "DD/MM/YYYY",
      "D/M/YYYY",
      "DD-MM-YYYY",
      "D-M-YYYY",
      "YYYY-MM-DD",
      "YYYY/MM/DD",
      "MM/DD/YYYY",
      "M/D/YYYY",
      "YYYY-MM-DDTHH:mm:ssZ",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "DD/MM/YYYY HH:mm",
      "DD-MM-YYYY HH:mm",
      "MM/DD/YYYY HH:mm",
      "YYYY-MM-DD HH:mm",
    ];
    for (const f of formats) {
      const d = dayjs(s, f, true);
      if (d.isValid()) return d;
    }
    // fallback: let dayjs try
    const d = dayjs(s);
    if (d.isValid()) return d;
  }
  return dayjs.invalid();
};

const ThreeMonthGuide = ({ reviews }) => {
  // Build labels for the last 3 FULL months: -3, -2, -1 (ignores current month)

  const [productFilter, setProductFilter] = useState("All");
  const [languageFilter, setLanguageFilter] = useState("All");

  const lastThreeMonths = useMemo(() => {
    const now = dayjs();
    const labels = [];
    for (let i = 3; i >= 1; i--) {
      labels.push(now.subtract(i, "month").format("MMM"));
    }
    return labels;
  }, []);

  const lastThreeMonthsReviews = useMemo(() => {
    const now = dayjs();
    return (
      reviews?.filter((review) => {
        const d = parseCommentDate(review?.[3]); // Comment Date
        if (!d?.isValid()) return false;

        const diffMonths =
          now.year() * 12 + now.month() - (d.year() * 12 + d.month());
        return diffMonths === 1 || diffMonths === 2 || diffMonths === 3;
      }) || []
    );
  }, [reviews]);

  const uniqueProducts = useMemo(() => {
    return Array.from(
      new Set(lastThreeMonthsReviews.map((r) => r?.[6]?.trim()).filter(Boolean))
    );
  }, [lastThreeMonthsReviews]);

  const uniqueLanguages = useMemo(() => {
    return Array.from(
      new Set(
        lastThreeMonthsReviews
          .map((r) => r?.[7]?.trim().toLowerCase())
          .filter((lang) => lang && lang !== "?")
      )
    );
  }, [lastThreeMonthsReviews]);

  const { averages, counts } = useMemo(() => {
    const now = dayjs();
    const sums = [0, 0, 0];
    const counts = [0, 0, 0];

    lastThreeMonthsReviews.forEach((review) => {
      const d = parseCommentDate(review?.[3]);
      const rating = parseFloat(review?.[4]);
      const productCode = review?.[6]?.trim();
      const language = review?.[7]?.trim().toLowerCase();

      if (!d?.isValid() || !isFinite(rating)) return;

      // ✅ Apply filters
      if (productFilter !== "All" && productCode !== productFilter) return;
      if (languageFilter !== "All" && language !== languageFilter) return;

      const diffMonths =
        now.year() * 12 + now.month() - (d.year() * 12 + d.month());
      if (diffMonths === 3) {
        sums[0] += rating;
        counts[0]++;
      } else if (diffMonths === 2) {
        sums[1] += rating;
        counts[1]++;
      } else if (diffMonths === 1) {
        sums[2] += rating;
        counts[2]++;
      }
    });

    const averages = sums.map((sum, i) =>
      counts[i] > 0 ? Math.round((sum / counts[i]) * 100) / 100 : 0
    );

    return { averages, counts };
  }, [lastThreeMonthsReviews, productFilter, languageFilter]);

  const validAvgs = averages.filter((x) => x > 0);
  let yMin = 4;
  if (validAvgs.length) {
    const actualMin = Math.min(...validAvgs);
    if (actualMin < 4) yMin = Math.floor(actualMin);
  }

  const barColors = averages.map((avg) => {
    if (avg >= 4.8) return "#4CAF50";
    if (avg >= 4.6) return "#FFD700";
    return "#FF4C4C";
  });

  const data = {
    labels: lastThreeMonths,
    datasets: [
      {
        label: "Average Rating",
        data: averages,
        backgroundColor: barColors,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Average Ratings Over the Last 3 Full Months",
      },
      tooltip: {
        callbacks: {
          afterBody: (items) => {
            const idx = items?.[0]?.dataIndex ?? 0;
            return `Count: ${counts[idx]}`;
          },
        },
      },
    },
    scales: {
      y: {
        min: yMin,
        max: 5,
        ticks: { stepSize: 0.1 },
      },
    },
  };

  return (
    <div className="three-bar-graph">
      <div className="three-filters">
        <label>
          Product Code:
          <select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
          >
            <option value="All">All</option>
            {uniqueProducts.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>

        <label>
          Language:
          <select
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
          >
            <option value="All">All</option>
            {uniqueLanguages.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
      </div>
      <Bar data={data} options={options} />
    </div>
  );
};

export default ThreeMonthGuide;
